import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Badge, Button, EmptyState, Input, Spinner } from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import type { AdminAuditListItem, AdminAuditListResponse } from '../features/audit/queries';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';

export const Route = createFileRoute('/_admin/audit')({
  component: AuditScreen,
});

function stringifyJson(value: unknown): string {
  if (value == null) return '(없음)';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function AuditScreen() {
  const [actorInput, setActorInput] = useState('');
  const [actionInput, setActionInput] = useState('');
  const [filters, setFilters] = useState<{ actorId: string; action: string }>({
    actorId: '',
    action: '',
  });
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const listQuery = useInfiniteQuery({
    queryKey: ['admin', 'audit', 'list', filters] as const,
    queryFn: ({ pageParam }) =>
      apiFetch<AdminAuditListResponse>('/v1/admin/audit-logs', {
        query: {
          actorId: filters.actorId || undefined,
          action: filters.action || undefined,
          cursor: pageParam ?? undefined,
        },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items: AdminAuditListItem[] = listQuery.data?.pages.flatMap((page) => page.items) ?? [];

  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applyFilters = () => {
    setFilters({ actorId: actorInput.trim(), action: actionInput.trim() });
  };

  return (
    <>
      <PageHeader title="감사 로그" description="어드민 조작 이력 (변경 전/후 기록)" />

      <form
        className="mb-4 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <div className="w-64">
          <Input
            type="search"
            placeholder="액터 ID"
            aria-label="액터 ID 필터"
            value={actorInput}
            onChange={(e) => setActorInput(e.target.value)}
            clearable
            clearLabel="지우기"
          />
        </div>
        <div className="w-56">
          <Input
            type="search"
            placeholder="액션 (예: order.transition)"
            aria-label="액션 필터"
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            clearable
            clearLabel="지우기"
          />
        </div>
        <Button variant="tertiary" size="sm" type="submit">
          검색
        </Button>
      </form>

      {listQuery.isPending ? (
        <div className="flex min-h-52 items-center justify-center">
          <Spinner label="감사 로그 불러오는 중" />
        </div>
      ) : listQuery.isError ? (
        <EmptyState
          title="감사 로그를 불러오지 못했습니다"
          description={
            listQuery.error instanceof ApiError ? listQuery.error.message : '잠시 후 다시 시도해 주세요'
          }
          action={
            <Button variant="tertiary" size="sm" onClick={() => void listQuery.refetch()}>
              다시 시도
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ScrollText size={40} strokeWidth={1.5} />}
          title="감사 로그가 없습니다"
          description="조건에 맞는 로그가 없습니다"
        />
      ) : (
        <>
          <Table>
            <Thead>
              <tr>
                <Th className="w-8" />
                <Th className="w-40">액터</Th>
                <Th>액션</Th>
                <Th>리소스</Th>
                <Th className="w-36">일시</Th>
              </tr>
            </Thead>
            <tbody>
              {items.map((log) => {
                const isOpen = expanded.has(log.id);
                return (
                  <Fragment key={log.id}>
                    <Tr
                      className="cursor-pointer hover:bg-subtle"
                      onClick={() => toggle(log.id)}
                    >
                      <Td>
                        {isOpen ? (
                          <ChevronDown className="size-4 text-fg-tertiary" aria-hidden="true" />
                        ) : (
                          <ChevronRight className="size-4 text-fg-tertiary" aria-hidden="true" />
                        )}
                      </Td>
                      <Td>
                        <span className="block font-medium text-fg">
                          {log.actor?.display_name ?? log.actor_id.slice(0, 8)}
                        </span>
                        <span className="block text-caption2 text-fg-tertiary">{log.actor_role}</span>
                      </Td>
                      <Td>
                        <Badge tone="info">{log.action}</Badge>
                      </Td>
                      <Td className="text-fg-secondary">
                        <span className="font-mono text-caption2">
                          {log.resource_type}/{log.resource_id.slice(0, 8)}
                        </span>
                      </Td>
                      <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
                        {formatDateTime(log.created_at)}
                      </Td>
                    </Tr>
                    {isOpen && (
                      <tr className="border-t border-line bg-subtle/50">
                        <td colSpan={5} className="px-3 py-3">
                          <div className="flex flex-col gap-3">
                            {log.reason != null && log.reason !== '' && (
                              <p className="text-caption1 text-fg-secondary">
                                <span className="text-fg-tertiary">사유: </span>
                                {log.reason}
                              </p>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="mb-1 text-caption2 font-medium text-fg-tertiary">변경 전</p>
                                <pre className="max-h-72 overflow-auto rounded-md border border-line bg-surface p-3 text-caption2 text-fg-secondary">
                                  {stringifyJson(log.before)}
                                </pre>
                              </div>
                              <div>
                                <p className="mb-1 text-caption2 font-medium text-fg-tertiary">변경 후</p>
                                <pre className="max-h-72 overflow-auto rounded-md border border-line bg-surface p-3 text-caption2 text-fg-secondary">
                                  {stringifyJson(log.after)}
                                </pre>
                              </div>
                            </div>
                            {(log.ip != null || log.request_id != null) && (
                              <p className="text-caption2 text-fg-tertiary">
                                {log.ip != null && <span>IP {log.ip}</span>}
                                {log.request_id != null && (
                                  <span className="ml-3">req {log.request_id}</span>
                                )}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>

          {listQuery.hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="tertiary"
                size="sm"
                loading={listQuery.isFetchingNextPage}
                onClick={() => void listQuery.fetchNextPage()}
              >
                더 보기
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}
